import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { User } from 'src/features/auth/types';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { toast } from 'src/hooks/useToast';
import { useUpdateUser } from 'src/features/users/api/useUpdateUser';
import { userMessages } from 'src/features/users/utils/messages';
import { updateUserFormSchema, UpdateUserFormSchemaValues } from 'src/features/users/utils/formSchema';
import UpdateUserForm from 'src/features/users/components/forms/UpdateUserForm';
import { LIST_USERS_RESOURCE } from 'src/features/users/api/useListUsers';
import { GET_USER_RESOURCE, useGetUser } from 'src/features/users/api/useGetUser';

interface UserFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUser: User;
    children?: React.ReactNode;
}

function UpdateUserFormModal<TData>({ semantics, selectedUser, children }: UserFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onError = useOnError();
    const { mutate, isPending } = useUpdateUser({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_USERS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [GET_USER_RESOURCE] });
            toast({
                title: formatMessage(userMessages.updateUserSuccess),
                description: formatMessage(userMessages.updateUserSuccessDescription, {
                    entry: success.name,
                }),
            });
            setIsModalOpen(false);
        },
        onError: (error) => onError(error, form.setError),
    });

    const { data: user } = useGetUser({
        userId: selectedUser?.id ?? '',
        config: { enabled: !!selectedUser?.id && isModalOpen },
    });

    const defaultValues = React.useMemo(
        () =>
            user
                ? { ...user, password: '', password_confirm: '' }
                : {
                      name: '',
                      username: '',
                      roles: [],
                      email: '',
                      password: '',
                      password_confirm: '',
                  },
        [user],
    );

    const form = useForm<UpdateUserFormSchemaValues>({
        resolver: zodResolver(updateUserFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const title = formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() });

    const onSubmit = React.useCallback(
        (data: UpdateUserFormSchemaValues) => mutate({ data, userId: selectedUser.id }),
        [mutate, selectedUser.id],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={user ? ICON_MAP.EDIT : ICON_MAP.ADD} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(userMessages.editUserSubtitle, { entry: selectedUser.name })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <UpdateUserForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || !form.formState.isDirty}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default UpdateUserFormModal;
